import React, { useEffect, useState } from 'react';
import axios from '../utils/axiosInstance';
import { BASE_URL } from '../config/constants';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { PageLoader } from '@/components/ui/page-loader';

interface CursorControlPanelProps {
  domain: string;
}

/**
 * Cursor Control — settings for the cursor effects on the public website.
 *
 * Currently supports the fluidic cursor on/off toggle. This is a standalone
 * panel (rendered as its own tab in SiteBrandingManager) so future cursor
 * options — style, color, speed, per-page effects — can be added here
 * without touching the Website Settings page itself.
 */
const CursorControlPanel: React.FC<CursorControlPanelProps> = ({ domain }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    if (!domain) return;
    setLoading(true);
    axios.get(BASE_URL + '/api/cursor-setting')
      .then(res => setEnabled(res.data.enabled !== false))
      .catch(() => setEnabled(true))
      .finally(() => setLoading(false));
  }, [domain]);

  const handleToggle = async (checked: boolean) => {
    const previous = enabled;
    // Optimistic update - flip immediately so the switch responds to the click
    setEnabled(checked);
    setSaving(true);
    try {
      const res = await axios.put(BASE_URL + '/api/cursor-setting', { enabled: checked });
      setEnabled(res.data.enabled !== false);
      toast.success(`Fluidic cursor ${checked ? 'activated' : 'deactivated'} on your website`);
    } catch (err: any) {
      // Revert on error
      setEnabled(previous);
      toast.error(err.response?.data?.message || 'Failed to update cursor settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="bg-card rounded-lg shadow p-4 sm:p-6">
        <h3 className="text-section-title mb-4">Cursor Control</h3>
        {loading ? (
          <PageLoader label="Loading cursor settings..." />
        ) : (
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-1">
              <Label className="form-label">Fluidic Cursor</Label>
              <p className="text-xs text-muted-foreground">
                The animated fluidic cursor that follows the pointer on your main website.
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={enabled}
                onCheckedChange={handleToggle}
                disabled={saving}
                aria-label="Toggle fluidic cursor"
              />
              <span className={enabled ? 'status-label-active' : 'status-label-inactive'}>
                {enabled ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CursorControlPanel;
